import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './ChatBox.css';

const ChatBox = ({ projectId, projectTitle, otherUser }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const currentUserId = JSON.parse(localStorage.getItem('user'))?.id;

  useEffect(() => {
    if (projectId) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000); // Poll every 3 seconds
      return () => clearInterval(interval);
    }
  }, [projectId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/chat/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/chat/project/${projectId}`, 
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' }) + ' ' +
           date.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return parts[0][0] + parts[1][0];
    }
    return name.substring(0, 2);
  };

  if (!projectId || !otherUser) {
    return (
      <div className="chat-container">
        <div className="no-chat">
          <div className="no-chat-icon">💬</div>
          <h3>Δεν υπάρχει ενεργή συνομιλία</h3>
          <p>Επιλέξτε ένα έργο για να ξεκινήσετε συνομιλία</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-header">
        <h3>{projectTitle}</h3>
        <p>Συνομιλία με {otherUser.name}</p>
      </div>

      <div className="chat-messages">
        {loading ? (
          <div className="empty-chat">Φόρτωση μηνυμάτων...</div>
        ) : messages.length === 0 ? (
          <div className="empty-chat">
            <div className="empty-chat-icon">💬</div>
            <p>Δεν υπάρχουν μηνύματα ακόμα</p>
            <p>Ξεκινήστε τη συνομιλία!</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div 
              key={msg.id} 
              className={`message ${msg.senderId === currentUserId ? 'sent' : 'received'}`}
            >
              <div className="message-avatar">
                {msg.senderId === currentUserId 
                  ? getInitials(JSON.parse(localStorage.getItem('user'))?.name)
                  : getInitials(otherUser.name)
                }
              </div>
              <div className="message-content">
                <div className="message-bubble">
                  {msg.message}
                </div>
                <div className="message-info">
                  <span>{formatTime(msg.createdAt)}</span>
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input">
        <form onSubmit={handleSend} className="input-wrapper">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
            placeholder="Πληκτρολογήστε το μήνυμά σας..."
            rows="1"
            disabled={sending}
          />
          <button 
            type="submit" 
            className="btn-send" 
            disabled={!newMessage.trim() || sending}
          >
            {sending ? 'Αποστολή...' : 'Αποστολή'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatBox;
